import React, { useEffect, useState, useImperativeHandle, forwardRef } from "react";
import axios from "axios";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export const EmailLogsCard = forwardRef(function EmailLogsCard({ authToken, isSuperAdmin, isAdmin }, ref) {
  const { toast } = useToast();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [retrying, setRetrying] = useState(false);

  const canDelete = isSuperAdmin || isAdmin;

  function authHeaders() {
    return authToken
      ? {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      : {};
  }

  async function loadLogs() {
    if (!authToken) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/api/email/logs`, authHeaders());
      setLogs(res.data || []);
    } catch (e) {
      console.error(e);
      const msg = e?.response?.data?.detail || "Sendeprotokolle konnten nicht geladen werden.";
      toast({ title: "Fehler", description: msg, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }

  // E-Mail-Details laden
  async function loadEmailDetail(logId) {
    setLoadingDetail(true);
    try {
      const res = await axios.get(`${API_BASE}/api/email-logs/${logId}`, authHeaders());
      setSelectedLog(res.data);
    } catch (e) {
      console.error(e);
      // Fallback: wenn kein Body vorhanden, zeige Info
      const log = logs.find(l => l.id === logId);
      if (log) {
        setSelectedLog({
          ...log,
          body: "<p><em>E-Mail-Inhalt wurde vor der Aktivierung dieser Funktion versendet und ist nicht gespeichert.</em></p>"
        });
      } else {
        toast({ title: "Fehler", description: "E-Mail konnte nicht geladen werden.", variant: "destructive" });
      }
    } finally {
      setLoadingDetail(false);
    }
  }

  // Expose refreshLogs to parent via ref
  useImperativeHandle(ref, () => ({
    refreshLogs: loadLogs
  }));

  useEffect(() => {
    loadLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken]);

  async function handleDelete(logId) {
    if (!canDelete) return;
    if (!window.confirm("Dieses Sendeprotokoll wirklich löschen?")) return;
    try {
      await axios.delete(`${API_BASE}/api/email/logs/${logId}`, authHeaders());
      setLogs((prev) => prev.filter((l) => l.id !== logId));
      toast({ title: "Sendeprotokoll gelöscht" });
    } catch (e) {
      console.error(e);
      const msg = e?.response?.data?.detail || "Sendeprotokoll konnte nicht gelöscht werden.";
      toast({ title: "Fehler", description: msg, variant: "destructive" });
    }
  }

  async function handleRetryFailed(logId) {
    if (!window.confirm("Fehlgeschlagene E-Mails jetzt erneut senden?")) return;
    setRetrying(true);
    try {
      const res = await axios.post(`${API_BASE}/api/email/logs/${logId}/retry`, {}, authHeaders());
      const { retry_sent, still_failed, still_failed_recipients } = res.data;
      if (still_failed === 0) {
        toast({ title: "Alle E-Mails erfolgreich nachgesendet!", description: `${retry_sent} E-Mails wurden zugestellt.` });
      } else {
        const names = still_failed_recipients.map(f => `${f.child_name} (${f.email})`).join("\n");
        toast({ title: `${retry_sent} gesendet, ${still_failed} weiterhin fehlgeschlagen`, description: names, variant: "destructive", duration: 15000 });
      }
      // Log-Detail neu laden
      const detail = await axios.get(`${API_BASE}/api/email/logs/${logId}/detail`, authHeaders());
      setSelectedLog(detail.data);
      loadLogs();
    } catch (e) {
      toast({ title: "Fehler", description: e?.response?.data?.detail || "Erneuter Versand fehlgeschlagen", variant: "destructive" });
    } finally {
      setRetrying(false);
    }
  }

  function formatDateTime(value) {
    try {
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return value;
      const dd = String(d.getDate()).padStart(2, "0");
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const yyyy = d.getFullYear();
      const hh = String(d.getHours()).padStart(2, "0");
      const mi = String(d.getMinutes()).padStart(2, "0");
      return `${dd}.${mm}.${yyyy}, ${hh}:${mi}`;
    } catch {
      return value;
    }
  }

  return (
    <>
      <Card className="border-slate-200 bg-white/90" data-testid="email-logs-card">
        <CardHeader>
          <CardTitle className="text-base font-semibold text-slate-900">Sendeprotokolle</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {logs.length === 0 ? (
            <p className="text-xs text-slate-500">Bisher wurden noch keine E-Mails über das System versendet.</p>
          ) : (
            <Table data-testid="email-logs-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Betreff</TableHead>
                  <TableHead>Von</TableHead>
                  <TableHead>Sendedatum</TableHead>
                  <TableHead>Gesendet an</TableHead>
                  <TableHead className="w-32 text-right">Aktionen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map((log) => (
                  <TableRow key={log.id} data-testid={`email-log-row-${log.id}`}>
                    <TableCell className="text-xs">{log.subject}</TableCell>
                    <TableCell className="text-xs font-medium text-violet-700">
                      {log.created_by_name || log.created_by_email?.split('@')[0] || '?'}
                    </TableCell>
                    <TableCell className="text-xs">{formatDateTime(log.created_at)}</TableCell>
                    <TableCell className="text-xs">
                      <span>an {log.sent_count} Empfänger</span>
                      {log.failed_count > 0 && (
                        <span className="text-red-600 font-semibold ml-1">
                          ({log.failed_count} fehlgeschlagen!)
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button
                        type="button"
                        variant="outline"
                        size="xs"
                        onClick={() => loadEmailDetail(log.id)}
                        data-testid={`email-log-view-${log.id}`}
                      >
                        Anzeigen
                      </Button>
                      {canDelete && (
                        <Button
                          type="button"
                          variant="outline"
                          size="xs"
                          onClick={() => handleDelete(log.id)}
                          data-testid={`email-log-delete-${log.id}`}
                        >
                          Löschen
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Dialog für E-Mail-Inhalt */}
      <Dialog open={!!selectedLog} onOpenChange={(open) => !open && setSelectedLog(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold">
              {selectedLog?.subject || "E-Mail"}
            </DialogTitle>
          </DialogHeader>
          {loadingDetail ? (
            <div className="py-8 text-center text-slate-500">Lädt...</div>
          ) : selectedLog && (
            <div className="space-y-4">
              <div className="flex flex-wrap gap-4 text-sm text-slate-600 border-b border-slate-100 pb-3">
                <div>
                  <span className="font-medium">Gesendet:</span>{" "}
                  {formatDateTime(selectedLog.created_at)}
                </div>
                <div>
                  <span className="font-medium">Von:</span>{" "}
                  {selectedLog.created_by_email || "System"}
                </div>
                <div>
                  <span className="font-medium">Erfolgreich:</span>{" "}
                  {selectedLog.sent_count} von {selectedLog.total_recipients}
                </div>
                {selectedLog.failed_count > 0 && (
                  <div className="text-red-600 font-semibold">
                    Fehlgeschlagen: {selectedLog.failed_count}
                  </div>
                )}
              </div>
              {selectedLog.failed_recipients && selectedLog.failed_recipients.length > 0 && (
                <div className="rounded-lg bg-red-50 border border-red-200 p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold text-red-800">Fehlgeschlagene Empfänger:</p>
                    <Button
                      size="sm"
                      className="bg-red-600 hover:bg-red-700 text-white text-xs"
                      disabled={retrying}
                      data-testid="retry-failed-btn"
                      onClick={() => handleRetryFailed(selectedLog.id)}
                    >
                      {retrying ? "Wird gesendet..." : `${selectedLog.failed_recipients.length} erneut senden`}
                    </Button>
                  </div>
                  {selectedLog.failed_recipients.map((f, i) => (
                    <p key={i} className="text-xs text-red-700">{f.child_name} – {f.email} <span className="text-red-400">({f.error})</span></p>
                  ))}
                </div>
              )}
              {selectedLog.retry_at && (
                <p className="text-xs text-slate-400">
                  Letzter Nachversand: {formatDateTime(selectedLog.retry_at)} durch {selectedLog.retry_by || "Admin"}
                </p>
              )}
              <div 
                className="prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: selectedLog.body || "<em>Kein Inhalt verfügbar</em>" }}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
});
